/* Created by Interactive 63 - https://interactive63.com */
/**
 * Brings SVGs into the editor from every source: the file picker, drag and drop, paste and the
 * bundled samples. It owns the import options and reports problems and import warnings as
 * toasts. An import starts a new project; the previous one stays in the recent projects list.
 */
import { importSvgWith, type ImportOptions } from '../core/import/import-core';
import { setExpanded } from './store/actions';
import { addSvgLayer } from './store/layer-actions';
import type { Store } from './store/store';
import { h } from './ui/dom';
import { isTextEntryTarget } from './shortcuts';
import { toast } from './ui/toast';
import { isProjectFileName } from './persistence/project-file';
import type { ProjectSession } from './persistence/session';

export class ImportController {
  readonly options: ImportOptions = { clean: true, simplify: true };
  private readonly importedListeners = new Set<() => void>();

  /**
   * Creates a controller that imports into the given store. With a session, the previous project
   * is saved before it is replaced and project files are opened as well as SVGs.
   */
  constructor(
    private readonly store: Store,
    private readonly session?: ProjectSession,
  ) {}

  /** Calls a function after each successful import. Returns a function that stops it. */
  onImported(listener: () => void): () => void {
    this.importedListeners.add(listener);
    return () => {
      this.importedListeners.delete(listener);
    };
  }

  /** Opens the operating system file picker for an SVG. */
  chooseFile(): void {
    const input = h('input', { type: 'file', accept: '.svg,image/svg+xml' });
    input.addEventListener('change', () => {
      const file = input.files?.[0];
      if (file) void this.importFile(file);
    });
    input.click();
  }

  /** Opens the file picker for an SVG to add to the open project as a new layer. */
  chooseLayerFile(): void {
    const input = h('input', { type: 'file', accept: '.svg,image/svg+xml' });
    input.addEventListener('change', () => {
      const file = input.files?.[0];
      if (file) void this.addLayerFile(file);
    });
    input.click();
  }

  /** Reads an SVG file and adds it to the open project as a new layer at its native size. */
  async addLayerFile(file: File): Promise<void> {
    if (!/\.svg$/i.test(file.name) && file.type !== 'image/svg+xml') {
      toast.warning("That file isn't an SVG.");
      return;
    }
    try {
      const clean = this.options.clean
        ? (await import('../core/import/clean')).cleanSvg
        : (markup: string) => markup;
      const result = importSvgWith(
        await file.text(),
        file.name,
        (markup) => clean(markup, { enabled: true }),
        this.options,
      );
      if (!result.ok) {
        toast.error(result.error);
        return;
      }
      addSvgLayer(this.store, result.project);
      toast.info(`Added ${result.project.name} as a new layer.`);
      for (const warning of result.warnings) toast.warning(warning);
    } catch (error) {
      this.reportFailure(error);
    }
  }

  /** Reads a file and imports it, rejecting anything that is not an SVG or project file. */
  async importFile(file: File): Promise<void> {
    if (this.session && isProjectFileName(file.name)) {
      await this.session.openProjectFile(file);
      return;
    }
    if (!/\.svg$/i.test(file.name) && file.type !== 'image/svg+xml') {
      toast.warning("That file isn't an SVG.");
      return;
    }
    try {
      await this.importText(await file.text(), file.name);
    } catch (error) {
      this.reportFailure(error);
    }
  }

  /** Fetches and imports one of the bundled sample SVGs. */
  async importSample(name: string): Promise<void> {
    try {
      const response = await fetch(`${import.meta.env.BASE_URL}samples/${name}.svg`);
      if (!response.ok) throw new Error(`Sample ${name} failed to load (${response.status})`);
      await this.importText(await response.text(), `${name}.svg`);
    } catch (error) {
      this.reportFailure(error);
    }
  }

  /** Imports SVG markup as a new project. The optimiser loads the first time it is needed. */
  async importText(text: string, fileName: string): Promise<void> {
    try {
      const clean = this.options.clean
        ? (await import('../core/import/clean')).cleanSvg
        : (markup: string) => markup;
      const result = importSvgWith(
        text,
        fileName,
        (markup) => clean(markup, { enabled: true }),
        this.options,
      );
      if (!result.ok) {
        toast.error(result.error);
        return;
      }
      if (this.session) this.session.install(result.project);
      else this.store.replaceProject(result.project);
      const topLevelGroups = result.project.rootIds.filter(
        (id) => (result.project.elements[id]?.childIds.length ?? 0) > 0,
      );
      setExpanded(this.store, topLevelGroups, true);
      for (const warning of result.warnings) toast.warning(warning);
      this.importedListeners.forEach((listener) => {
        listener();
      });
    } catch (error) {
      this.reportFailure(error);
    }
  }

  /** Listens for dropped files and pasted markup anywhere in the window. */
  attach(): () => void {
    const overlay = h('div', { class: 'drop-overlay', hidden: true }, 'Drop to import');
    document.body.append(overlay);
    let depth = 0;

    /** Whether a drag carries files rather than text or links. */
    const hasFiles = (event: DragEvent): boolean =>
      event.dataTransfer?.types.includes('Files') ?? false;

    const onDragEnter = (event: DragEvent): void => {
      if (!hasFiles(event)) return;
      event.preventDefault();
      depth += 1;
      overlay.hidden = false;
    };
    const onDragOver = (event: DragEvent): void => {
      if (hasFiles(event)) event.preventDefault();
    };
    const onDragLeave = (event: DragEvent): void => {
      if (!hasFiles(event)) return;
      depth = Math.max(0, depth - 1);
      if (depth === 0) overlay.hidden = true;
    };
    const onDrop = (event: DragEvent): void => {
      if (!hasFiles(event)) return;
      event.preventDefault();
      depth = 0;
      overlay.hidden = true;
      const file = event.dataTransfer?.files[0];
      if (file) void this.importFile(file);
    };
    const onPaste = (event: ClipboardEvent): void => {
      if (isTextEntryTarget(event.target)) return;
      const text = event.clipboardData?.getData('text/plain').trim() ?? '';
      if (!text.startsWith('<svg') && !text.startsWith('<?xml')) return;
      event.preventDefault();
      void this.importText(text, '');
    };

    window.addEventListener('dragenter', onDragEnter);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('drop', onDrop);
    document.addEventListener('paste', onPaste);
    return () => {
      window.removeEventListener('dragenter', onDragEnter);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('drop', onDrop);
      document.removeEventListener('paste', onPaste);
      overlay.remove();
    };
  }

  /** Shows a friendly error and logs the details for debugging. */
  private reportFailure(error: unknown): void {
    toast.error('Something went wrong importing this SVG.');
    console.error('SVG import failed', error);
  }
}
